<template>
  <div v-if="basicData" class="order-form__base-info order-base-info">
    <div class="order-base-info__img">
      <img src="~@/assets/kanister/Canister.png" />
    </div>
    <div class="order-base-info__title">
      <h2>Rowa Dose Canister</h2>
      <p>Article Number: {{ basicData.articleNumber }}</p>
    </div>
    <div class="order-base-info__right">
      <div class="order-base-info__select">
        <label class="label">
          {{ $t("page.order.submit.machine.referencenumber") }}
        </label>
        <v-input
          block
          v-model="referenceNumber"
          :placeholder="$t('common.typehere')"
          :redBorder="$v.referenceNumber && !$v.referenceNumber.required"
        />
        <div v-if="$v.referenceNumber && !$v.referenceNumber.required" class="popup popup--error">
          {{ $t("common.required") }}
        </div>
      </div>
      <div class="order-base-info__select">
        <label class="label">
          {{ $t("page.order.submit.machine.machinenumber") }}
        </label>
        <v-select
          title-property="name"
          value-property="id"
          v-model="machineNumber"
          :options="machines"
          :placeholder="$t('common.choose')"
          @input="changeMachineId($event)"
          cancelable
        />
      </div>
      <div
        v-if="freeItemsLeft && freeItemsTotal"
        class="order-base-info__checkbox"
        :class="{
          'order-base-info__checkbox--disabled': !couldUseFreeContingent,
        }"
      >
        <label>
          <v-checkbox
            type="checkbox"
            v-model="useFreeContingent"
            :disabled="!couldUseFreeContingent"
            @change="onCheckboxChange($event)"
          />
          <p>
            {{ $t("page.order.submit.use.free.contingent")}}
            <span>{{ itemsLeft }}/{{ freeItemsTotal }}</span>
          </p>
        </label>
        <div class="tooltip-predefined">{{ $t("page.order.submit.use.free.contingent.exceeded")}}</div>
      </div>
    </div>
    <v-free-contingent-confirm-popup
      v-if="showConfirmPopup"
      class="free-contingent-popup"
      @close="closeConfirmPopup()"
      @confirm="submitConfirmPopup()"
    />
  </div>
  <v-loader v-else />
</template>

<script>
import { mapActions, mapMutations, mapGetters } from "vuex";
import { VSelect, VCheckbox, VLoader, VInput } from "shared";
import VFreeContingentConfirmPopup from "@/components/order-submit/v-order-free-contingent-confirm-popup";
import { required } from "vuelidate/lib/validators";

export default {
  name: "VOrderSubmitMachine",
  components: {
    VSelect,
    VCheckbox,
    VLoader,
    VInput,
    VFreeContingentConfirmPopup,
  },
  props: {
    freeItemsLeft: {
      type: Number,
      default: null,
    },
    freeItemsTotal: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      useFreeContingent: false,
      showConfirmPopup: false,
    };
  },
  computed: {
    ...mapGetters("order", ["basicData", "machines", "onProductionAmount"]),
    referenceNumber: {
      get() {
        return this.$store.state.order.referenceNumber;
      },
      set(value) {
        if (this.$v.referenceNumber) {
          this.$v.referenceNumber.$touch();
        }
        this.setReferenceNumber(value);
      },
    },
    machineNumber: {
      get() {
        return this.$store.state.order.machineNumber;
      },
      set(value) {
        this.setMachineNumber(value);
      },
    },
    itemsLeft() {
      return this.useFreeContingent && this.couldUseFreeContingent
        ? this.freeItemsLeft - this.onProductionAmount
        : this.freeItemsLeft;
    },
    couldUseFreeContingent() {
      return this.freeItemsLeft - this.onProductionAmount >= 0;
    },
  },
  methods: {
    ...mapActions({
      onFreeContingentChange: "order/onFreeContingentChange",
      onMachineIdChange: "order/onMachineIdChange",
    }),
    ...mapMutations({
      setReferenceNumber: "order/setReferenceNumber",
      setMachineNumber: "order/setMachineNumber",
    }),
    changeFreeContingent($event) {
      this.onFreeContingentChange($event);
    },
    changeMachineId($event) {
      this.onMachineIdChange($event);
    },
    closeConfirmPopup() {
      this.showConfirmPopup = false;
      this.useFreeContingent = false;
    },
    submitConfirmPopup() {
      this.onFreeContingentChange(true);
      this.useFreeContingent = true;
      this.showConfirmPopup = false;
    },
    onCheckboxChange($event) {
      if ($event.target.checked) {
        $event.preventDefault();
        this.showConfirmPopup = true;
      } else {
        this.onFreeContingentChange(false);
      }
    },
  },
  watch: {
    couldUseFreeContingent(value) {
      if (!value) {
        this.useFreeContingent = false;
        this.changeFreeContingent(this.useFreeContingent);
      }
    },
  },
  validations() {
    return this.basicData.checkoutDisplayMode === "us"
      ? {
          referenceNumber: {
            required,
          },
        }
      : {};
  },
};
</script>

<style scoped lang="scss">
.order-base-info {
  display: flex;
  padding: 30px 71px 40px 75px;
  background: #fff;
  border: 1px solid #e4e4e4;
  margin-bottom: 16px;
  @media all and (max-width: 1200px) {
    padding-left: 30px;
    padding-right: 30px;
  }
  &__title {
    margin: 22px 0 0 37px;
    h2 {
      font-size: 24px;
      line-height: 26px;
      letter-spacing: 0.8px;
    }
    p {
      font-size: 13px;
      line-height: 14px;
      letter-spacing: 0.464286px;
      margin: 8px 0;
    }
  }
  &__right {
    margin-left: auto;
  }
  &__select {
    position: relative;
    margin: 6px 0 25px 7px;
    display: inline-block;
    min-width: 248px;
    @media all and (max-width: 1200px) {
      min-width: 180px;
    }
    .label {
      font-size: 13px;
      color: #999999;
      margin-bottom: 8px;
      display: inline-block;
      font-weight: bold;
      letter-spacing: 0.4px;
    }
    &::v-deep .input {
      background: #fdfdfd;
      border-radius: 3px;
      height: 41px;
      padding-left: 16px;
    }
  }
  &__checkbox {
    margin: 9px 0 0 0;
    text-align: right;
    display: flex;
    position: relative;
    label {
      display: flex;
      align-items: center;
      margin-left: auto;
      cursor: pointer;
      p {
        font-weight: bold;
        font-size: 13px;
        line-height: 16px;
        text-align: right;
        letter-spacing: 0.7px;
        color: #333333;
        margin: 0 0 0 12px;
        span {
          color: #004593;
        }
      }
    }
    &--disabled {
      /*opacity: 0.4;*/
      label {
        opacity: 0.5;
        &:hover {
          & + .tooltip-predefined {
            opacity: 1 !important;
            visibility: visible;
          }
        }
      }
    }
  }
}
::v-deep .options {
  width: 100%;
}
.tooltip-predefined {
  background: #fff url("~@/assets/icons/icon-lock.svg") 10px 12px;
  box-shadow: 0px 2px 4px rgba(153, 153, 153, 0.4);
  border-radius: 6px 6px 6px 0px;
  position: absolute !important;
  right: 0;
  bottom: 120%;
  background-repeat: no-repeat;
  padding: 18px 15px 15px 47px;
  opacity: 0;
  font-size: 14px;
  visibility: hidden;
  z-index: 10;
  transition: all 0.3s;
  color: #999999;
  white-space: nowrap;
}

// TODO: take to the common styles or style differently
@import "../../styles/variables";
.popup {
  position: absolute;
  right: 0;
  top: calc(100% + 3px);
  background: $color-white;
  color: $color-medium-grey;
  box-shadow: 0 2px 4px rgba(153, 153, 153, 0.4);
  border-radius: 6px 1px 6px 6px;
  font-size: 13px;
  white-space: nowrap;
  line-height: 40px;
  z-index: 10;

  & + & {
    top: calc(100% + 47px);
  }

  &--error {
    padding: 0 15px 0 50px;
    background: $color-white url("~@/assets/icons/icon-error.svg") 16px center no-repeat;
    color: $color-red;
  }
  &.free-contingent-popup {
    top: 0;
    white-space: normal;
  }
}
</style>
